import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES } from "@/data";
import { scrollTo } from "@/components/Nav";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay, className = "" }) {
    return (
        <span className="block overflow-hidden pb-[0.08em]">
            <motion.span
                className={`block will-change-transform ${className}`}
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.15, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Cover() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

    return (
        <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="cover-section">
            <motion.div
                className="absolute inset-0 scale-[1.18] bg-cover bg-center will-change-transform"
                style={{ backgroundImage: `url(${IMAGES.cover})`, y: bgY }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#020D18]/80 via-[#020D18]/55 to-[#020D18]" />

            <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full pt-24">
                <h1
                    className="font-display font-semibold tracking-tight text-6xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.95]"
                    data-testid="cover-title"
                >
                    <MaskedLine delay={0.35}>River</MaskedLine>
                    <MaskedLine delay={0.53} className="text-sky-200">Guardian</MaskedLine>
                </h1>
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.3, delay: 1.25, ease: EASE }}
                    className="mt-8 h-px w-56 origin-left bg-gradient-to-r from-sky-300 via-sky-300/60 to-transparent"
                    aria-hidden="true"
                />
                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
                    className="mt-7 text-lg md:text-2xl text-slate-200 max-w-2xl leading-relaxed"
                    data-testid="cover-tagline"
                >
                    A self-funding river restoration company for Sydney, beginning with the Parramatta River.
                </motion.p>
                <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.5 }}
                    onClick={() => scrollTo("hero")}
                    data-testid="cover-scroll-cue"
                    className="mt-14 inline-flex flex-col items-center gap-2 text-sky-200/90 hover:text-sky-200 transition-colors"
                >
                    <span className="text-xs font-semibold tracking-[0.3em] uppercase">The story below</span>
                    <ChevronDown className="w-5 h-5 animate-bounce" />
                </motion.button>
            </motion.div>
        </section>
    );
}
